// static/js/components/ChatThreadList.jsx

/**
 * ChatThreadList Component
 * -------------------------
 * Component for displaying a list of chat threads in a sidebar.
 * This component allows users to view their chat threads ordered by last access time.
 * It supports interaction functionalities such as selecting a thread to view, renaming,
 * deleting, and creating new chat threads.
 *
 * Features:
 * - Dynamically fetch chat threads on initial load and upon specified refresh conditions.
 * - Display chat threads with options to expand/collapse the list for better navigation.
 * - Provide UI elements for thread actions like rename and delete via a popup menu.
 * - Handle loading and error states, providing user feedback during data fetching and operations.
 * - Ensure CSRF token handling for secure API requests.
 * - Utilize modern React hooks for state and effect management.
 *
 * The component integrates alert displays for errors and utilizes a loader spinner for loading states.
 * It employs conditional rendering to manage user interactions and display states effectively,
 * enhancing the user experience by making chat navigation seamless and responsive.
 */

import React, { useState, useEffect } from 'react';
import { MessagesSquare, MoreVertical, Plus, Loader } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';

const ChatThreadList = ({ 
  onThreadSelect, 
  currentThreadId,
  csrfToken,
  onNewChat,
  shouldRefresh // to trigger refresh
}) => {
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activePopup, setActivePopup] = useState(null);
  const [isRenaming, setIsRenaming] = useState(false);
  const [newName, setNewName] = useState('');
  const [isExpanded, setIsExpanded] = useState(false); // State for show more/less functionality


  // Fetch threads function
  const fetchThreads = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/chat/threads', {
        headers: {
          'X-CSRFToken': csrfToken
        },
        credentials: 'include'
      });

      if (response.status === 401) {
        // Sets empty thread list for unauthorized users instead of showing error
        setThreads([]);
        return;
      }

      // console.log('Threads API Response:', response);
  
      if (!response.ok) {
        throw new Error(`Failed to fetch threads: ${response.status}`);
      }

      const data = await response.json();

      setThreads(data.threads);

    } catch (err) {
      setError('Failed to load chat history. Sorry!');
      console.error('Error fetching threads:', err);
    } finally {
      setLoading(false);
    }
  };

  // Handles refresh trigger
  useEffect(() => {
    if (shouldRefresh) {
      fetchThreads();
    }
  }, [shouldRefresh]);


  // Initial fetch on mount, and also handles thread rename events
  useEffect(() => {
    fetchThreads();

    // Listens for thread rename events
    const handleThreadRenamed = () => {
      const { threadId, threadName } = event.detail;
      // Update the thread name in the local state immediately
      setThreads(prevThreads => {
        // If prevThreads is empty, create a new array with the renamed thread
        if (!prevThreads || prevThreads.length === 0) {
          return [{
            thread_id: threadId,
            thread_name: threadName
          }];
        }
        // Otherwise update existing thread
        return prevThreads.map(thread => 
          thread.thread_id === threadId 
            ? { ...thread, thread_name: threadName }
            : thread
        );
      });
    };

    window.addEventListener('threadRenamed', handleThreadRenamed);

    return () => {
      window.removeEventListener('threadRenamed', handleThreadRenamed);
    };

  }, []);

  // Handle thread renaming
  const handleRename = async (threadId, newThreadName) => {
    if (!newThreadName || newThreadName.length > 255) {
      setError('Thread name must be between 1 and 255 characters');
      return;
    }

    try {
      const response = await fetch('/api/chat/rename', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrfToken
        },
        credentials: 'include',
        body: JSON.stringify({ threadId, newName: newThreadName })
      });

      if (!response.ok) throw new Error('Failed to rename thread');
  
      // Refresh threads list
      await fetchThreads();
      setActivePopup(null);
      setIsRenaming(false);
      setNewName('');
    } catch (err) {
      setError('Failed to rename thread');
      console.error('Error renaming thread:', err);
    }
  };

  // Handle thread deletion
  const handleDelete = async (threadId) => {
    try {
      const response = await fetch('/api/chat/delete', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrfToken
        },
        credentials: 'include',
        body: JSON.stringify({ threadId })
      });

      if (!response.ok) throw new Error('Failed to delete thread');
  
      // Refresh threads list
      await fetchThreads();
      setActivePopup(null);
    } catch (err) {
      setError('Failed to delete thread');
      console.error('Error deleting thread:', err);
    }
  };

  return (
    <div className="flex flex-col space-y-4 p-4">
      {/* New Chat button */}
      <div className="flex justify-start px-2">
        <button
          onClick={onNewChat}
          className="inline-flex items-center space-x-2 px-3 py-1.5 bg-primary-700/10 rounded-full hover:bg-emerald-900/20 transition-colors text-gray-300 w-auto"
        >
          <Plus size={16} />
          <span className="text-xs whitespace-nowrap">New Chat</span>
        </button>
      </div>

      {/* Recent label */}
      <div className="text-sm font-large text-primary-text/60 px-2">Recent Chats</div>

      {/* Loading state */}
      {loading && (
        <div className="flex items-center justify-center py-4">
          <Loader className="animate-spin" size={20} />
        </div>
      )}

      {/* Error display */}
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Thread list */}
      <div className="space-y-2">
        {/* Conditionally show all threads or just first 5 based on expanded state */}
        {threads.slice(0, isExpanded ? threads.length : 5).map((thread) => (
          <div
            key={thread.thread_id}
            className={`group flex items-center justify-between p-2 rounded-lg hover:bg-primary-700/10 cursor-pointer ${
              currentThreadId === thread.thread_id ? 'bg-primary-700/20' : ''
            }`}
          >
            <div
              className="flex items-center space-x-2 flex-1 min-w-0"
              onClick={() => onThreadSelect(thread.thread_id)}
            >
              <MessagesSquare size={20} className="flex-shrink-0" />
              <span className="truncate text-xs text-gray-200" title={thread.thread_name}>
                {thread.thread_name}
              </span>
            </div>

            {/* Thread actions popup trigger */}
            <div className="relative">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setActivePopup(activePopup === thread.thread_id ? null : thread.thread_id);
                }}
                className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-primary-700/20 rounded"
              >
                <MoreVertical size={16} className="text-gray-300" />
              </button>

              {/* Popup menu */}
              {activePopup === thread.thread_id && (
                <div className="absolute -left-20 mt-2 w-32 rounded-md shadow-lg bg-primary-bg border border-primary-border z-50">
                  <div className="py-1">
                    {isRenaming ? (
                      <form
                        onSubmit={(e) => {
                          e.preventDefault();
                          handleRename(thread.thread_id, newName);
                        }}
                        className="p-2"
                      >
                        <input
                          type="text"
                          value={newName}
                          onChange={(e) => setNewName(e.target.value)}
                          placeholder="New name..."
                          maxLength={255}
                          className="w-full p-1 border rounded text-xs"
                          autoFocus
                        />
                      </form>
                    ) : (
                      <>
                        <button
                          onClick={() => {
                            setNewName(thread.thread_name);
                            setIsRenaming(true);
                          }}
                          className="block w-full text-left px-3 py-1.5 hover:bg-primary-700/10 text-xs text-gray-300"
                        >
                          Rename
                        </button>
                        <button
                          onClick={() => handleDelete(thread.thread_id)}
                          className="block w-full text-left px-3 py-1.5 text-red-400 hover:bg-primary-700/10 text-xs"
                        >
                          Delete
                        </button>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}

        {/* Show more/less toggle button */}
        {threads.length > 5 && (
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="w-full mt-2 py-2 px-3 text-xs text-gray-300 hover:bg-primary-700/10 rounded-lg flex items-center justify-center space-x-1"
          >
            {isExpanded ? (
              <>
                <span>^</span>
                <span>Show less</span>
              </>
            ) : (
              <>
                <span>v</span>
                <span>Show more</span>
              </>
            )}
          </button>
        )}

      </div>
    </div>
  );
};

export default ChatThreadList;
